import React, {
  useCallback, useEffect, useMemo, useState, Fragment, ReactElement,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Card from '../../../../components/card/card.component';
import Button from '../../../../components/button/button.component';
import ModalButtonBlock from '../../../../components/modal-button-block/modal-button-block.component';
import StatusBar from '../../../../components/status-bar/status-bar.component';
import Loader from '../../../../components/loader/loader.component';
import ErrorBlock from '../../../../components/error-block/error-block.component';
import ModalLabel from '../../../../components/modal-label/modal-label.component';
import ModalLayout from '../../../../components/modal-layout/modal-layout.component';
import ModalDescription from '../../../../components/modal-description/modal-description.component';
import ModalFieldBlock from '../../../../components/modal-field-block/modal-field-block.component';
import FieldSelect from '../../../../components/field-select/field-select.component';
import FieldUpload from '../../../../components/field-upload/field-upload.component';
import MenuItemList from '../../../../components/menu-item-list/menu-item-list.component';
import MainService from '../../../../services/main-service';
import StructureService from '../../../../services/structure-service';
import useCurrentUserPermissions from '../../../../hooks/use-current-user-permissions';
import importData from './import-modal.action';
import SPREADSHEET_NAME_DEFAULT from '../../../../constants/system/spreadsheet-names';

const ImportModal: ReactMemoComponent = React.memo((props: ReactComponentProps): ReactElement => {
  const {
    isOpen,
    importTab,
    importEntityType,
    dealId,
    defaultImportType,
    importTypeList,
    importFormatList,
    onSuccess,
    onCloseModal,
  } = props;

  const [importFile, setImportFile]: [File | null, Function] = useState(null);
  const [importType, setImportType]: [string | null, Function] = useState(null);
  const [isOpenMessage, setIsOpenMessage]: [boolean, Function] = useState(false);
  const [statusMessage, setStatusMessage]: [string, Function] = useState('');
  const [errorMessage, setErrorMessage]: [string | null, Function] = useState(null);

  const dispatch: ReduxDispatch = useDispatch();
  const permissionList: string[] = useCurrentUserPermissions();
  const isLoading: boolean = useSelector(({ importModal }: Reducers): boolean => importModal.isLoading);

  const spreadsheetNamePlatform: string | undefined = MainService.PLATFORM_TITLE;

  const menuItemImportTypeList: ReactElement[] = useMemo((): ReactElement[] => MenuItemList({
    optionItemValueKey: 'name',
    optionItemLabelKey: 'label',
    optionList: importTypeList,
    selectedValue: importType,
  }), [importTypeList, importType]);

  const closeModal: Function = useCallback((): void => {
    setImportFile(null);
    setErrorMessage(null);

    setStatusMessage('');
    setIsOpenMessage(false);

    if (onCloseModal) {
      onCloseModal();
    }
  }, []);

  const sendForm: MouseEventHandlerFunction = useCallback((): Promise<ActionCallback> | null => {
    if (!importFile) {
      return null;
    }

    return new Promise((resolve: ActionResolveCallback): void => {
      dispatch(importData({
        importForm: {
          entityType: importEntityType,
          importFile,
          importType,
          importTab,
          dealId,
        },
      }, (response: ActionResponse): void => {
        if (onCloseModal) {
          onCloseModal();
        }

        setImportFile(null);
        setErrorMessage(null);

        setStatusMessage('Imported successfully');
        setIsOpenMessage(true);

        if (onSuccess) {
          onSuccess();
        }

        resolve(response);
      }, (exception: ServerError): void => {
        if (exception && exception.message) {
          setErrorMessage(exception.message);
        }
      }));
    });
  }, [importFile, importEntityType, importType, importTab, dealId]);

  const closeMessage: Function = useCallback((): void => {
    setIsOpenMessage(false);
    setStatusMessage('');
  }, []);

  const onImportFileDelete: Function = useCallback((): void => {
    setImportFile(null);
    setErrorMessage(null);
  }, []);

  const onImportFileChange: Function = useCallback((file: File): void => {
    setImportFile(file);
    setErrorMessage(null);
  }, []);

  const onImportFileError: Function = useCallback((error: string): void => {
    setErrorMessage(error);
  }, []);

  const importTypeChange: Function = useCallback((importTypeName: string): void => {
    setImportType(importTypeName);
  }, []);

  useEffect((): void => {
    if (importTypeList) {
      let currentImportType: string | null = importType;
      importTypeList.forEach((importTypeItem: ImportTypeItem, importTypeIndex: number): void => {
        if (importTypeItem.permission && !StructureService.checkPermission(importTypeItem.permission, permissionList)) {
          delete importTypeList[importTypeIndex];
        }

        if (!currentImportType
          && ((defaultImportType && defaultImportType === importTypeItem.name) || !defaultImportType)
          && ((importTypeItem.permission && StructureService.checkPermission(importTypeItem.permission, permissionList))
            || !importTypeItem.permission)) {
          setImportType(importTypeItem.name);
          currentImportType = importTypeItem.name;
        }
      });
    }
  }, [importTypeList]);

  return (
    <Fragment>
      <ModalLayout isOpen={isOpen} title="Import Data" onCloseModal={closeModal}>
        <Card isContainer>
          {
            importTypeList && (
              <Fragment>
                <ModalLabel>
                  Select type of import:
                </ModalLabel>
                <ModalFieldBlock>
                  <FieldSelect
                    isPlain
                    isBordered
                    value={importType}
                    onChange={(event: EventSelectChange, value: string): void => {
                      importTypeChange(value);
                    }}
                  >
                    {menuItemImportTypeList}
                  </FieldSelect>
                </ModalFieldBlock>
              </Fragment>
            )
          }
          <ModalFieldBlock>
            <FieldUpload
              isPlain
              isDroppable
              uploadFile={importFile}
              buttonLabel="Upload File"
              acceptedExtensionList={importFormatList && importFormatList.length
                ? importFormatList.map((formatItem: FormatItem): string => formatItem.name) : ''}
              onFileChange={onImportFileChange}
              onFileDelete={onImportFileDelete}
              onError={onImportFileError}
            />
          </ModalFieldBlock>
          {
            importFormatList && !!importFormatList.length && (
              <ModalDescription>
                {
                  `Accepted formats: `
                  + `${importFormatList.map((formatItem: FormatItem): string => formatItem.label).join(', ')}.`
                }
              </ModalDescription>
            )
          }
          <ModalDescription>
            Spreadsheet file must include a worksheet with
            {spreadsheetNamePlatform ? ` a name "${spreadsheetNamePlatform}" ` : ' '}
            or default name "{SPREADSHEET_NAME_DEFAULT}".
          </ModalDescription>
        </Card>
        <ModalButtonBlock>
          <Button isActive isDisabled={!importFile} onClick={sendForm}>
            Import
          </Button>
          {
            isLoading && (
              <Loader isInline isModal isSmall />
            )
          }
        </ModalButtonBlock>
        {
          !!errorMessage && (
            <ErrorBlock errorMessage={errorMessage} />
          )
        }
      </ModalLayout>
      <StatusBar isOpen={isOpenMessage} message={statusMessage} onClose={closeMessage} />
    </Fragment>
  );
});

export default ImportModal;
